<script setup>
import { computed } from 'vue';
import { AiGenerativeIcon, Comment01Icon } from '@hugeicons/core-free-icons';
import HugeIcon from 'dashboard/components-next/icon/HugeIcon.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';

const props = defineProps({
  expanded: { type: Boolean, default: false },
  showSummarizeButton: { type: Boolean, default: false },
  summarizeDisabled: { type: Boolean, default: false },
  summarizeLoading: { type: Boolean, default: false },
});

const emit = defineEmits(['update:expanded', 'summarize']);

const toggle = () => {
  emit('update:expanded', !props.expanded);
};

const onSummarize = () => {
  emit('summarize');
};

const noteColumnClass = computed(() =>
  props.expanded ? 'min-w-0 w-full flex-1' : 'min-w-0 flex-1'
);
</script>

<template>
  <div class="flex w-full min-w-0 gap-2">
    <div class="min-w-0 transition-colors" :class="noteColumnClass">
      <button
        type="button"
        class="flex h-8 min-h-8 w-full items-center gap-2 rounded-lg px-3 text-left text-[0.9375rem] font-medium text-n-slate-12 bg-transparent transition-colors hover:bg-n-alpha-2 active:bg-n-alpha-2"
        :aria-expanded="expanded"
        :aria-label="
          expanded
            ? $t('CONVERSATION.REPLYBOX.NOTE_PANEL.COLLAPSE')
            : $t('CONVERSATION.REPLYBOX.NOTE_PANEL.EXPAND')
        "
        @click="toggle"
      >
        <span class="flex min-w-0 items-center gap-2">
          <HugeIcon
            :icon="Comment01Icon"
            :size="18"
            class="shrink-0 text-n-slate-12"
            aria-hidden="true"
          />
          <span class="truncate">{{
            $t('CONVERSATION.REPLYBOX.NOTE_PANEL.ADD_NOTE')
          }}</span>
        </span>
      </button>
      <div
        v-show="expanded"
        class="border-t border-n-amber-12/5 bg-n-solid-amber px-3 pb-3 pt-2 dark:border-n-amber-3/10"
      >
        <p class="mb-2 text-xs leading-relaxed text-n-amber-11">
          {{ $t('CONVERSATION.REPLYBOX.NOTE_PANEL.HINT') }}
        </p>
        <div id="reply-composer-teleport-note" class="min-h-[1px]" />
      </div>
    </div>

    <div v-if="showSummarizeButton && !expanded" class="shrink-0 self-start">
      <button
        type="button"
        class="inline-flex h-8 min-h-8 max-w-full items-center gap-2 whitespace-nowrap rounded-lg px-3 text-left text-[0.9375rem] font-medium text-n-violet-11 bg-transparent transition-colors hover:bg-n-alpha-2 active:bg-n-alpha-2 disabled:cursor-not-allowed disabled:opacity-50"
        :disabled="summarizeDisabled || summarizeLoading"
        :aria-label="$t('INTEGRATION_SETTINGS.OPEN_AI.REPLY_OPTIONS.SUMMARIZE')"
        @click="onSummarize"
      >
        <Spinner
          v-if="summarizeLoading"
          :size="18"
          class="shrink-0 text-n-violet-11"
        />
        <HugeIcon
          v-else
          :icon="AiGenerativeIcon"
          :size="18"
          class="shrink-0 text-n-violet-11"
          aria-hidden="true"
        />
        <span>{{ $t('CONVERSATION.REPLYBOX.NOTE_PANEL.SUMMARIZE') }}</span>
      </button>
    </div>
  </div>
</template>
